import { useEffect, useState } from 'react';
import { api, type Historial as DatosHistorial, type Partido, type Yo } from '../services/api';
import { Aviso, Boton, BotonVolver, Cargando, Tarjeta, Titulo, Vacio } from '../components/ui';
import { PlacaNoche } from '../components/PlacaNoche';
import { MarcadorPartido } from '../components/MarcadorPartido';
import { fechaHora, mesEnPalabras } from '../nucleo/fechas';

/**
 * Todos los partidos de un mes, agrupados por día, en todos los estados.
 *
 * Inicio muestra los últimos diez y esconde los rechazados; acá va todo,
 * porque se viene a buscar algo puntual ("el del jueves") o a revisar qué se
 * cargó. Cada partido dice quién lo cargó y a qué hora sin tener que abrirlo;
 * tocándolo se ve la historia completa.
 */
export function Historial({
  yo,
  mes,
  setMes,
  onVolver,
  onVerPartido,
}: {
  yo: Yo;
  /** Vive en App para que volver del detalle deje el mismo mes a la vista. */
  mes: string | undefined;
  setMes: (mes: string) => void;
  onVolver: () => void;
  onVerPartido: (id: number) => void;
}) {
  const [datos, setDatos] = useState<DatosHistorial | null>(null);
  const [error, setError] = useState<string | null>(null);
  // La noche cuya placa se esta mirando.
  const [placaDe, setPlacaDe] = useState<string | null>(null);

  useEffect(() => {
    setDatos(null);
    setError(null);
    api
      .historial(mes)
      .then(setDatos)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Error'));
  }, [mes]);

  if (error) {
    return (
      <div className="flex flex-col gap-4">
        <BotonVolver onClick={onVolver} />
        <Aviso tono="error">{error}</Aviso>
      </div>
    );
  }
  if (!datos) return <Cargando que="Buscando partidos" />;

  const dias = agruparPorDia(datos.partidos);
  const cuenta = (estado: string) => datos.partidos.filter((p) => p.estado === estado).length;
  const rechazados = cuenta('rechazado');
  const anulados = cuenta('anulado');

  return (
    <div className="flex flex-col gap-4">
      <BotonVolver onClick={onVolver} />

      <div className="flex items-center justify-between gap-3">
        <Titulo>Historial</Titulo>
        {datos.meses_con_datos.length > 1 && (
          <select
            value={datos.mes}
            onChange={(e) => setMes(e.target.value)}
            className="min-h-10 rounded-lg border border-(--color-borde) bg-(--color-panel)
              px-3 text-sm outline-none"
          >
            {datos.meses_con_datos.map((m) => (
              <option key={m} value={m}>
                {mesEnPalabras(m)}
              </option>
            ))}
          </select>
        )}
      </div>

      {datos.partidos.length === 0 ? (
        <Vacio>No hay partidos cargados en {datos.titulo}.</Vacio>
      ) : (
        <>
          <p className="-mt-2 text-sm text-(--color-apagado)">
            {datos.partidos.length === 1 ? '1 partido' : `${datos.partidos.length} partidos`} en{' '}
            {datos.titulo}
            {rechazados > 0 && ` · ${rechazados} rechazado${rechazados > 1 ? 's' : ''}`}
            {anulados > 0 && ` · ${anulados} anulado${anulados > 1 ? 's' : ''}`}. Tocá uno
            para ver quién lo cargó y cuándo.
          </p>

          {dias.map(([fecha, partidos]) => (
            <section key={fecha} className="flex flex-col gap-2">
              <div className="flex min-h-8 items-center justify-between gap-2">
                <Titulo chico>{diaDe(fecha)}</Titulo>
                {partidos.some((p) => p.estado === 'confirmado') && placaDe !== fecha && (
                  <Boton mini tipo="secundario" onClick={() => setPlacaDe(fecha)}>
                    📸 Placa
                  </Boton>
                )}
              </div>
              {placaDe === fecha && <PlacaNoche fecha={fecha} onCerrar={() => setPlacaDe(null)} />}
              {partidos.map((partido) => (
                <Tarjeta key={partido.id} compacta>
                  <MarcadorPartido
                    partido={partido}
                    yoId={yo.jugador.id}
                    onAbrir={() => onVerPartido(partido.id)}
                  />
                  <p className="mt-1 text-xs text-(--color-apagado)">{quienYCuando(partido)}</p>
                </Tarjeta>
              ))}
            </section>
          ))}
        </>
      )}
    </div>
  );
}

/** Ya vienen ordenados del más nuevo al más viejo: se respeta ese orden. */
function agruparPorDia(partidos: Partido[]): [string, Partido[]][] {
  const porDia = new Map<string, Partido[]>();
  for (const p of partidos) {
    const lista = porDia.get(p.jugado_el) ?? [];
    lista.push(p);
    porDia.set(p.jugado_el, lista);
  }
  return [...porDia.entries()];
}

function diaDe(fecha: string): string {
  const { dia } = fechaHora(Date.parse(`${fecha}T15:00:00Z`) / 1000);
  return dia.charAt(0).toUpperCase() + dia.slice(1);
}

/** 'Cargó Pedro a las 21:14', con el día si no es el mismo en que se jugó. */
function quienYCuando(partido: Partido): string {
  const c = fechaHora(partido.creado_en);
  const cuando = c.fecha === partido.jugado_el ? `a las ${c.hora}` : `el ${c.dia} a las ${c.hora}`;
  return `Cargó ${partido.cargado_por_nombre} ${cuando}`;
}
